(() => {
  const config = window.themeConfig || {};
  const SELECTORS = {
    drawer: '[data-mobile-drawer]',
    drawerOpen: '[data-drawer-open]',
    drawerClose: '[data-drawer-close]',
    header: '[data-site-header]',
    headerLogo: '[data-header-logo]',
    announcement: '[data-announcement-bar]',
    announcementMessage: '[data-announcement-message]',
    quickAddForm: '[data-quick-add-form]',
    cartDialog: '[data-cart-dialog]',
    cartOpen: '[data-cart-open]',
    cartClose: '[data-cart-close]',
    cartCount: '[data-cart-count]',
    searchDialog: '[data-search-dialog]',
    searchOpen: '[data-search-open]',
    searchClose: '[data-search-close]',
    productGallery: '[data-product-gallery]',
    productForm: '[data-product-form]',
    productSticky: '[data-product-sticky]',
    quantitySelector: '[data-quantity-selector]',
    productRecommendations: '[data-product-recommendations]',
    recentlyViewed: '[data-recently-viewed]',
  };

  const lockScroll = () => document.documentElement.classList.add('is-scroll-locked');
  const unlockScroll = () => {
    if (!document.querySelector('dialog[open]')) {
      document.documentElement.classList.remove('is-scroll-locked');
    }
  };

  const formatMoney = (amount) =>
    new Intl.NumberFormat(config.locale || 'en-IN', {
      style: 'currency',
      currency: config.currency || 'INR',
      maximumFractionDigits: 2,
    }).format((amount || 0) / 100);

  const openDialog = (dialog, trigger, focusTarget) => {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    dialog.returnFocus = trigger;
    dialog.showModal();
    trigger?.setAttribute('aria-expanded', 'true');
    lockScroll();
    window.requestAnimationFrame(() => focusTarget?.focus());
  };

  const closeDialog = (dialog) => {
    if (dialog?.open) dialog.close();
  };

  const initializeDialogDismissal = (dialog, closeButton) => {
    if (!dialog || dialog.dataset.dismissInitialized === 'true') return;
    dialog.dataset.dismissInitialized = 'true';
    closeButton?.addEventListener('click', () => closeDialog(dialog));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });
    dialog.addEventListener('close', () => {
      dialog.returnFocus?.setAttribute?.('aria-expanded', 'false');
      unlockScroll();
      dialog.returnFocus?.focus?.();
    });
    dialog.addEventListener('cancel', unlockScroll);
  };

  const initializeDrawer = (drawer) => {
    if (drawer.dataset.initialized === 'true') return;
    const header = drawer.closest('[data-site-header]');
    const openButton = header?.querySelector(SELECTORS.drawerOpen);
    const closeButton = drawer.querySelector(SELECTORS.drawerClose);
    if (!openButton || !closeButton) return;
    drawer.dataset.initialized = 'true';
    initializeDialogDismissal(drawer, closeButton);
    openButton.addEventListener('click', () => openDialog(drawer, openButton, closeButton));
  };

  const initializeAnnouncement = (announcement) => {
    if (announcement.dataset.initialized === 'true') return;
    if (announcement.dataset.rotationEnabled !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const messages = [...announcement.querySelectorAll(SELECTORS.announcementMessage)];
    const interval = Number.parseInt(announcement.dataset.rotationInterval, 10);
    if (messages.length < 2 || !Number.isFinite(interval)) return;

    announcement.dataset.initialized = 'true';
    let activeIndex = 0;
    let timer;
    const showNext = () => {
      messages[activeIndex].hidden = true;
      activeIndex = (activeIndex + 1) % messages.length;
      messages[activeIndex].hidden = false;
    };
    const start = () => {
      if (!timer) timer = window.setInterval(showNext, interval);
    };
    const stop = () => {
      window.clearInterval(timer);
      timer = undefined;
    };

    announcement.addEventListener('mouseenter', stop);
    announcement.addEventListener('mouseleave', start);
    announcement.addEventListener('focusin', stop);
    announcement.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    start();
  };

  const initializeHeader = (header) => {
    if (header.dataset.headerInitialized === 'true') return;
    header.dataset.headerInitialized = 'true';

    const logo = header.querySelector(SELECTORS.headerLogo);
    const showLogoFallback = () => header.classList.add('site-header--logo-failed');
    if (logo) {
      if (logo.complete && logo.naturalWidth === 0) showLogoFallback();
      logo.addEventListener('error', showLogoFallback);
    }

    const updateHeaderState = () => {
      const announcement = document.querySelector(SELECTORS.announcement);
      const threshold = announcement?.offsetHeight || 0;
      header.classList.toggle('site-header--scrolled', window.scrollY > threshold);
    };

    updateHeaderState();
    window.addEventListener('scroll', updateHeaderState, { passive: true });
    window.addEventListener('resize', updateHeaderState);
  };

  const createPredictiveCard = (resource) => {
    const link = document.createElement('a');
    link.className = 'predictive-result';
    link.href = resource.url;

    if (resource.featured_image?.url || resource.image) {
      const image = document.createElement('img');
      image.src = resource.featured_image?.url || resource.image;
      image.alt = resource.featured_image?.alt || resource.title || '';
      image.loading = 'lazy';
      link.append(image);
    }

    const title = document.createElement('strong');
    title.textContent = resource.title;
    link.append(title);

    if (resource.price) {
      const price = document.createElement('span');
      price.textContent = resource.price;
      link.append(price);
    }

    return link;
  };

  const renderPredictiveResults = (container, data) => {
    container.replaceChildren();
    const results = data?.resources?.results || {};
    const resources = [
      ...(results.products || []),
      ...(results.collections || []),
      ...(results.articles || []),
    ];

    if (!resources.length) {
      const empty = document.createElement('p');
      empty.className = 'search-overlay__hint';
      empty.textContent = config.strings?.searchNoResults || 'No results found.';
      container.append(empty);
      return;
    }

    const heading = document.createElement('p');
    heading.className = 'predictive-results__heading';
    heading.textContent = 'Suggested results';
    const grid = document.createElement('div');
    grid.className = 'predictive-results__grid';
    resources.slice(0, 8).forEach((resource) => grid.append(createPredictiveCard(resource)));
    container.append(heading, grid);
  };

  const initializeSearch = (dialog) => {
    if (dialog.dataset.initialized === 'true') return;
    const input = dialog.querySelector('[data-predictive-search-input]');
    const results = dialog.querySelector('[data-predictive-search-results]');
    const closeButton = dialog.querySelector(SELECTORS.searchClose);
    const triggers = document.querySelectorAll(`[aria-controls="${dialog.id}"], ${SELECTORS.searchOpen}`);
    if (!input || !results) return;

    dialog.dataset.initialized = 'true';
    initializeDialogDismissal(dialog, closeButton);
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => openDialog(dialog, trigger, input));
    });

    let timer;
    let controller;
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      controller?.abort();
      const query = input.value.trim();
      if (query.length < 2) return;

      timer = window.setTimeout(async () => {
        controller = new AbortController();
        results.setAttribute('aria-busy', 'true');
        try {
          const base = config.routes?.searchSuggest || '/search/suggest';
          const url = `${base}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=8`;
          const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Search failed');
          renderPredictiveResults(results, await response.json());
        } catch (error) {
          if (error.name !== 'AbortError') renderPredictiveResults(results, {});
        } finally {
          results.removeAttribute('aria-busy');
        }
      }, 220);
    });
  };

  const fetchCart = async () => {
    const response = await fetch(`${config.routes?.cart || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  };

  const updateCartCount = (cart) => {
    document.querySelectorAll(SELECTORS.cartCount).forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.toggle('site-header__cart-count--empty', cart.item_count === 0);
    });
    document.querySelectorAll('[data-cart-title-count]').forEach((count) => {
      count.textContent = `(${cart.item_count})`;
    });
    document.querySelectorAll(SELECTORS.cartOpen).forEach((button) => {
      const template = config.strings?.cartLabel || 'Cart, __COUNT__ items';
      button.setAttribute('aria-label', template.replace('__COUNT__', cart.item_count));
    });
  };

  const createCartLine = (item) => {
    const article = document.createElement('article');
    article.className = 'cart-drawer-item';
    article.dataset.cartLine = '';
    article.dataset.lineKey = item.key;

    const imageLink = document.createElement('a');
    imageLink.className = 'cart-drawer-item__image';
    imageLink.href = item.url;
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.product_title;
      image.loading = 'lazy';
      imageLink.append(image);
    }

    const content = document.createElement('div');
    content.className = 'cart-drawer-item__content';
    const details = document.createElement('div');
    const title = document.createElement('a');
    title.className = 'cart-drawer-item__title';
    title.href = item.url;
    title.textContent = item.product_title;
    details.append(title);
    if (item.variant_title) {
      const variant = document.createElement('p');
      variant.textContent = item.variant_title;
      details.append(variant);
    }
    const price = document.createElement('strong');
    price.textContent = formatMoney(item.final_line_price);
    details.append(price);

    const actions = document.createElement('div');
    actions.className = 'cart-drawer-item__actions';
    const quantity = document.createElement('div');
    quantity.className = 'quantity-control';
    quantity.dataset.quantityControl = '';
    const decrease = document.createElement('button');
    decrease.type = 'button';
    decrease.dataset.cartDecrease = '';
    decrease.setAttribute('aria-label', `Decrease quantity for ${item.product_title}`);
    decrease.textContent = '−';
    const input = document.createElement('input');
    input.type = 'number';
    input.min = '0';
    input.value = item.quantity;
    input.dataset.cartQuantity = '';
    input.setAttribute('aria-label', `Quantity for ${item.product_title}`);
    const increase = document.createElement('button');
    increase.type = 'button';
    increase.dataset.cartIncrease = '';
    increase.setAttribute('aria-label', `Increase quantity for ${item.product_title}`);
    increase.textContent = '+';
    quantity.append(decrease, input, increase);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cart-drawer-item__remove';
    remove.dataset.cartRemove = '';
    remove.setAttribute('aria-label', `Remove ${item.product_title}`);
    remove.textContent = '×';
    actions.append(quantity, remove);
    content.append(details, actions);
    article.append(imageLink, content);
    return article;
  };

  const renderCart = (dialog, cart) => {
    updateCartCount(cart);
    const body = dialog.querySelector('[data-cart-drawer-body]');
    const recommendations = body?.querySelector('.cart-drawer__recommendations');
    body?.querySelector('[data-cart-items], [data-cart-empty]')?.remove();

    if (cart.item_count > 0) {
      const items = document.createElement('div');
      items.className = 'cart-drawer__items';
      items.dataset.cartItems = '';
      cart.items.forEach((item) => items.append(createCartLine(item)));
      body?.insertBefore(items, recommendations || null);
    } else {
      const empty = document.createElement('div');
      empty.className = 'cart-drawer__empty';
      empty.dataset.cartEmpty = '';
      const heading = document.createElement('h3');
      heading.textContent = 'Your cart is empty';
      const text = document.createElement('p');
      text.textContent = 'Explore X Emirates skincare essentials.';
      const link = document.createElement('a');
      link.className = 'button button--primary';
      link.href = '/collections/all';
      link.textContent = 'Shop best sellers';
      empty.append(heading, text, link);
      body?.insertBefore(empty, recommendations || null);
    }

    const footer = dialog.querySelector('[data-cart-footer]');
    footer?.classList.toggle('cart-drawer__footer--hidden', cart.item_count === 0);
    const subtotal = dialog.querySelector('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

    const progress = dialog.querySelector('[data-shipping-progress]');
    const threshold = Number(progress?.dataset.threshold || 0);
    const remaining = threshold - cart.total_price;
    const message = progress?.querySelector('[data-shipping-message]');
    const bar = progress?.querySelector('[data-shipping-bar]');
    if (message) {
      message.textContent =
        remaining > 0
          ? (config.strings?.shippingRemaining || 'Add __AMOUNT__ for free delivery').replace('__AMOUNT__', formatMoney(remaining))
          : config.strings?.shippingUnlocked || 'You unlocked free delivery';
    }
    if (bar) bar.style.width = `${threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 100}%`;

    const status = dialog.querySelector('[data-cart-status]');
    if (status) status.textContent = config.strings?.cartUpdated || 'Cart updated.';
  };

  const changeCartLine = async (key, quantity) => {
    const response = await fetch(`${config.routes?.cartChange || '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!response.ok) throw new Error('Unable to update cart');
    return response.json();
  };

  const initializeCart = (dialog) => {
    if (dialog.dataset.initialized === 'true') return;
    const closeButton = dialog.querySelector(SELECTORS.cartClose);
    const triggers = document.querySelectorAll(`[aria-controls="${dialog.id}"], ${SELECTORS.cartOpen}`);
    dialog.dataset.initialized = 'true';
    initializeDialogDismissal(dialog, closeButton);

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', async () => {
        openDialog(dialog, trigger, closeButton);
        try {
          renderCart(dialog, await fetchCart());
        } catch (error) {
          // The server-rendered cart remains available.
        }
      });
    });

    dialog.addEventListener('click', async (event) => {
      const line = event.target.closest('[data-cart-line]');
      if (!line) return;
      const input = line.querySelector('[data-cart-quantity]');
      let quantity = Number(input?.value || 0);
      if (event.target.closest('[data-cart-increase]')) quantity += 1;
      if (event.target.closest('[data-cart-decrease]')) quantity = Math.max(0, quantity - 1);
      if (event.target.closest('[data-cart-remove]')) quantity = 0;
      if (quantity === Number(input?.value) && !event.target.closest('[data-cart-remove]')) return;
      try {
        dialog.setAttribute('aria-busy', 'true');
        renderCart(dialog, await changeCartLine(line.dataset.lineKey, quantity));
      } finally {
        dialog.removeAttribute('aria-busy');
      }
    });

    dialog.addEventListener('change', async (event) => {
      const input = event.target.closest('[data-cart-quantity]');
      const line = input?.closest('[data-cart-line]');
      if (!input || !line) return;
      renderCart(dialog, await changeCartLine(line.dataset.lineKey, Math.max(0, Number(input.value))));
    });
  };

  const initializeQuickAdd = (form) => {
    if (form.dataset.initialized === 'true') return;
    const button = form.querySelector('button[type="submit"]');
    const label = form.querySelector('[data-quick-add-label]');
    const status = form.querySelector('[data-quick-add-status]');
    if (!button || !label || !status) return;

    form.dataset.initialized = 'true';
    let resetTimer;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      window.clearTimeout(resetTimer);
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      label.textContent = button.dataset.labelLoading;
      try {
        const response = await fetch(form.action, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error('Unable to add item');
        const cart = await fetchCart();
        label.textContent = button.dataset.labelSuccess;
        status.textContent = button.dataset.labelSuccess;
        updateCartCount(cart);
        const dialog = document.querySelector(SELECTORS.cartDialog);
        if (dialog) {
          renderCart(dialog, cart);
          openDialog(dialog, button, dialog.querySelector(SELECTORS.cartClose));
        }
      } catch (error) {
        label.textContent = button.dataset.labelError;
        status.textContent = button.dataset.labelError;
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        resetTimer = window.setTimeout(() => (label.textContent = button.dataset.labelDefault), 2200);
      }
    });
  };

  const showProductMedia = (gallery, mediaId) => {
    if (!gallery || !mediaId) return;
    gallery.querySelectorAll('[data-media-id]').forEach((media) => {
      media.hidden = media.dataset.mediaId !== String(mediaId);
    });
    gallery.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
      thumbnail.setAttribute('aria-pressed', String(thumbnail.dataset.mediaThumbnail === String(mediaId)));
    });
  };

  const getActiveMediaId = (gallery) => gallery?.querySelector('[data-media-id]:not([hidden])')?.dataset.mediaId;

  const showAdjacentProductMedia = (gallery, direction) => {
    const thumbnails = [...gallery.querySelectorAll('[data-media-thumbnail]')];
    if (!thumbnails.length) return;
    const activeId = getActiveMediaId(gallery);
    const activeIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.dataset.mediaThumbnail === activeId));
    const nextIndex = (activeIndex + direction + thumbnails.length) % thumbnails.length;
    showProductMedia(gallery, thumbnails[nextIndex].dataset.mediaThumbnail);
  };

  const initializeProductGallery = (gallery) => {
    if (gallery.dataset.initialized === 'true') return;
    gallery.dataset.initialized = 'true';
    gallery.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('[data-media-thumbnail]');
      if (thumbnail) showProductMedia(gallery, thumbnail.dataset.mediaThumbnail);
      if (event.target.closest('[data-media-next]')) showAdjacentProductMedia(gallery, 1);
      if (event.target.closest('[data-media-previous]')) showAdjacentProductMedia(gallery, -1);
    });
  };

  const initializeQuantitySelector = (selector) => {
    if (selector.dataset.initialized === 'true') return;
    const input = selector.querySelector('input[name="quantity"]');
    if (!input) return;
    selector.dataset.initialized = 'true';
    selector.addEventListener('click', (event) => {
      if (event.target.closest('[data-quantity-increase]')) input.value = Math.max(1, Number(input.value) + 1);
      if (event.target.closest('[data-quantity-decrease]')) input.value = Math.max(1, Number(input.value) - 1);
    });
  };

  const initializeProductForm = (form) => {
    if (form.dataset.productInitialized === 'true') return;
    const variantInput = form.querySelector('[data-product-variant]');
    if (!variantInput) return;
    form.dataset.productInitialized = 'true';
    const buyNowButton = form.querySelector('.xem-buy-now-to-cart');
    const buyNowLabel = buyNowButton?.querySelector('[data-buy-now-label]');
    const status = form.querySelector('[data-quick-add-status]');

    const getVariantAvailability = () => {
      const option = variantInput.selectedOptions?.[0];
      return option ? option.dataset.available !== 'false' : variantInput.dataset.available !== 'false';
    };

    const syncProductBuyNowButton = (isAvailable) => {
      if (!buyNowButton) return;
      buyNowButton.disabled = !isAvailable;
      buyNowButton.setAttribute('aria-disabled', String(!isAvailable));
    };

    syncProductBuyNowButton(getVariantAvailability());

    buyNowButton?.addEventListener('click', async (event) => {
      event.preventDefault();
      if (buyNowButton.disabled || buyNowButton.getAttribute('aria-busy') === 'true') return;

      const variantId = variantInput.value;
      const quantityInput = form.querySelector('input[name="quantity"]');
      const quantity = Math.max(1, Number(quantityInput?.value || 1));

      if (!variantId || !getVariantAvailability()) {
        syncProductBuyNowButton(false);
        if (status) status.textContent = buyNowButton.dataset.labelError || 'Unable to add item';
        return;
      }

      buyNowButton.disabled = true;
      buyNowButton.setAttribute('aria-busy', 'true');
      if (buyNowLabel) buyNowLabel.textContent = buyNowButton.dataset.labelLoading || 'Adding...';
      if (status) status.textContent = '';

      try {
        const response = await fetch(config.routes?.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: variantId, quantity }),
        });

        if (!response.ok) throw new Error('Unable to add item');
        window.location.href = config.routes?.cart || '/cart';
      } catch (error) {
        if (status) status.textContent = buyNowButton.dataset.labelError || 'Unable to add item';
        buyNowButton.disabled = false;
        buyNowButton.removeAttribute('aria-busy');
        if (buyNowLabel) buyNowLabel.textContent = buyNowButton.dataset.labelDefault || 'Buy it now';
      }
    });

    variantInput.addEventListener('change', () => {
      const option = variantInput.selectedOptions?.[0];
      if (!option) return;
      const price = Number(option.dataset.price || 0);
      const comparePrice = Number(option.dataset.comparePrice || 0);
      const priceContainer = form.closest('[data-product-section]')?.querySelector('[data-product-price]');
      const currentPrice = form.closest('[data-product-section]')?.querySelector('[data-product-current-price]');
      const comparePriceElement = form.closest('[data-product-section]')?.querySelector('[data-product-compare-price]');
      const savings = form.closest('[data-product-section]')?.querySelector('[data-product-savings]');
      const stickyPrice = form.closest('[data-product-section]')?.querySelector('[data-sticky-price]');
      const gallery = form.closest('[data-product-section]')?.querySelector(SELECTORS.productGallery);
      const submitButton = form.querySelector('button[type="submit"]');
      const submitLabel = submitButton?.querySelector('[data-quick-add-label]');
      const isAvailable = option.dataset.available !== 'false';

      if (priceContainer && !currentPrice) {
        priceContainer.innerHTML = `<div class="price"><span class="price__current">${formatMoney(price)}</span>${
          comparePrice > price
            ? `<span class="price__compare"><s>${formatMoney(comparePrice)}</s></span>`
            : ''
        }</div>`;
      }
      if (currentPrice) currentPrice.textContent = formatMoney(price);
      if (comparePriceElement) {
        comparePriceElement.hidden = comparePrice <= price;
        comparePriceElement.innerHTML = comparePrice > price ? `<s>${formatMoney(comparePrice)}</s>` : '';
      }
      if (savings) {
        const savingsAmount = Math.max(0, comparePrice - price);
        savings.hidden = savingsAmount <= 0;
        savings.textContent = `You save ${formatMoney(savingsAmount)} on this product`;
      }
      if (stickyPrice) stickyPrice.textContent = formatMoney(price);
      if (submitButton) submitButton.disabled = !isAvailable;
      syncProductBuyNowButton(isAvailable);
      if (submitLabel) submitLabel.textContent = isAvailable ? submitButton.dataset.labelDefault : config.strings?.soldOut || 'Sold out';
      showProductMedia(gallery, option.dataset.mediaId);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantInput.value);
      window.history.replaceState({}, '', url);
    });
  };

  const initializeProductSticky = (bar) => {
    if (bar.dataset.initialized === 'true') return;
    const section = bar.closest('[data-product-section]');
    const form = section?.querySelector('[data-product-form]');
    const submitButton = form?.querySelector('button[type="submit"]');
    if (!section || !submitButton || !('IntersectionObserver' in window)) {
      bar.dataset.visible = 'true';
      return;
    }
    bar.dataset.initialized = 'true';
    const observer = new IntersectionObserver(
      ([entry]) => {
        bar.dataset.visible = String(!entry.isIntersecting);
      },
      { rootMargin: '0px 0px -72px 0px', threshold: 0 }
    );
    observer.observe(submitButton);
  };

  const initializeRecommendations = async (section) => {
    if (section.dataset.initialized === 'true' || !section.dataset.url) return;
    section.dataset.initialized = 'true';
    try {
      const response = await fetch(section.dataset.url);
      if (!response.ok) return;
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = documentFragment.querySelector(SELECTORS.productRecommendations);
      if (!replacement?.innerHTML.trim()) return;
      section.innerHTML = replacement.innerHTML;
      section.querySelectorAll(SELECTORS.quickAddForm).forEach(initializeQuickAdd);
    } catch (error) {
      // Recommendations are progressive enhancement.
    }
  };

  const initializeRecentlyViewed = async (section) => {
    if (section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';
    const currentHandle = section.dataset.currentHandle;
    const limit = Number(section.dataset.limit || 4);
    const storageKey = 'x-emirates-recent-products';
    let handles = [];
    try {
      handles = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    } catch (error) {
      handles = [];
    }

    const previousHandles = handles.filter((handle) => handle && handle !== currentHandle).slice(0, limit);
    const updatedHandles = [currentHandle, ...previousHandles].filter(Boolean).slice(0, 8);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(updatedHandles));
    } catch (error) {
      return;
    }

    if (!previousHandles.length) return;
    const products = await Promise.all(
      previousHandles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
          return response.ok ? response.json() : null;
        } catch (error) {
          return null;
        }
      }),
    );

    const grid = section.querySelector('[data-recently-viewed-grid]');
    products.filter(Boolean).forEach((product) => {
      const card = document.createElement('a');
      card.className = 'recent-product-card';
      card.href = product.url;
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = product.featured_image;
        image.alt = product.title;
        image.loading = 'lazy';
        card.append(image);
      }
      const title = document.createElement('strong');
      title.textContent = product.title;
      const price = document.createElement('span');
      price.textContent = formatMoney(product.price);
      card.append(title, price);
      grid?.append(card);
    });
    if (grid?.children.length) section.hidden = false;
  };

  const initializeMegaMenus = (root) => {
    root.querySelectorAll('[data-mega-menu]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        document.querySelectorAll('[data-mega-menu][open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      });
    });
  };

  const initialize = (root = document) => {
    root.querySelectorAll(SELECTORS.drawer).forEach(initializeDrawer);
    root.querySelectorAll(SELECTORS.header).forEach(initializeHeader);
    root.querySelectorAll(SELECTORS.announcement).forEach(initializeAnnouncement);
    root.querySelectorAll(SELECTORS.searchDialog).forEach(initializeSearch);
    root.querySelectorAll(SELECTORS.cartDialog).forEach(initializeCart);
    root.querySelectorAll(SELECTORS.quickAddForm).forEach(initializeQuickAdd);
    root.querySelectorAll(SELECTORS.productGallery).forEach(initializeProductGallery);
    root.querySelectorAll(SELECTORS.quantitySelector).forEach(initializeQuantitySelector);
    root.querySelectorAll(SELECTORS.productForm).forEach(initializeProductForm);
    root.querySelectorAll(SELECTORS.productSticky).forEach(initializeProductSticky);
    root.querySelectorAll(SELECTORS.productRecommendations).forEach(initializeRecommendations);
    root.querySelectorAll(SELECTORS.recentlyViewed).forEach(initializeRecentlyViewed);
    initializeMegaMenus(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
